import { useEffect, useRef, useState, useCallback } from 'react';
import io, { Socket } from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/auth.service';

interface SocketHook {
    socket: Socket | null;
    connected: boolean;
    emit: (event: string, data?: any) => void;
    on: (event: string, handler: (data: any) => void) => void;
    off: (event: string, handler?: (data: any) => void) => void;
}

export const useSocket = (): SocketHook => {
    const socketRef = useRef<Socket | null>(null);
    const [connected, setConnected] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        if (!user) return;

        const token = authService.getAccessToken();
        if (!token) return;

        // Initialize socket connection
        const socket = io(authService.getApiUrl(), {
            auth: {
                token
            },
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 5
        });

        socketRef.current = socket;

        // Connection event handlers
        socket.on('connect', () => {
            console.log('Socket connected');
            setConnected(true);
        });

        socket.on('disconnect', () => {
            console.log('Socket disconnected');
            setConnected(false);
        });

        socket.on('connect_error', (error) => {
            console.error('Socket connection error:', error.message);
            setConnected(false);
        });

        // Cleanup on unmount
        return () => {
            socket.disconnect();
            socketRef.current = null;
        };
    }, [user]);

    const emit = useCallback((event: string, data?: any) => {
        if (socketRef.current && connected) {
            socketRef.current.emit(event, data);
        }
    }, [connected]);

    const on = useCallback((event: string, handler: (data: any) => void) => {
        if (socketRef.current) {
            socketRef.current.on(event, handler);
        }
    }, []);

    const off = useCallback((event: string, handler?: (data: any) => void) => {
        if (socketRef.current) {
            if (handler) {
                socketRef.current.off(event, handler);
            } else {
                socketRef.current.off(event);
            }
        }
    }, []);

    return {
        socket: socketRef.current,
        connected,
        emit,
        on,
        off
    };
};

// Specific socket hooks for different features

export const useDocumentSocket = (documentId?: string) => {
    const { socket, connected, emit, on, off } = useSocket();
    const [documentStatus, setDocumentStatus] = useState<string | null>(null);
    const [progress, setProgress] = useState<number>(0);

    useEffect(() => {
        if (!connected || !documentId) return;

        // Subscribe to document updates
        emit('subscribe:document', documentId);

        // Listen for document events
        const handleProcessing = (data: any) => {
            setDocumentStatus('processing');
            setProgress(data.progress || 0);
        };

        const handleCompleted = (data: any) => {
            setDocumentStatus('completed');
            setProgress(100);
        };

        const handleFailed = (data: any) => {
            setDocumentStatus('failed');
            setProgress(0);
        };

        on('document:processing', handleProcessing);
        on('document:completed', handleCompleted);
        on('document:failed', handleFailed);

        // Cleanup
        return () => {
            emit('unsubscribe:document', documentId);
            off('document:processing', handleProcessing);
            off('document:completed', handleCompleted);
            off('document:failed', handleFailed);
        };
    }, [connected, documentId, emit, on, off]);

    return { documentStatus, progress };
};

export const useQueueSocket = () => {
    const { socket, connected, emit, on, off } = useSocket();
    const [queueUpdates, setQueueUpdates] = useState<any[]>([]);

    useEffect(() => {
        if (!connected) return;

        // Subscribe to queue updates
        emit('subscribe:queue');

        // Listen for queue events
        const handleQueueUpdate = (data: any) => {
            setQueueUpdates(prev => [data, ...prev].slice(0, 50)); // Keep last 50 updates
        };

        on('queue:update', handleQueueUpdate);

        // Cleanup
        return () => {
            off('queue:update', handleQueueUpdate);
        };
    }, [connected, emit, on, off]);

    return { queueUpdates };
};

export const useNotificationSocket = () => {
    const { socket, connected, on, off } = useSocket();
    const [notifications, setNotifications] = useState<any[]>([]);

    useEffect(() => {
        if (!connected) return;

        // Listen for various notification events
        const handleDocumentQueued = (data: any) => {
            setNotifications(prev => [{
                type: 'info',
                message: `Document "${data.fileName}" has been queued for processing`,
                timestamp: data.timestamp,
                ...data
            }, ...prev]);
        };

        const handleDocumentCompleted = (data: any) => {
            setNotifications(prev => [{
                type: 'success',
                message: `Document "${data.fileName}" has been processed successfully`,
                timestamp: data.timestamp,
                ...data
            }, ...prev]);
        };

        const handleDocumentFailed = (data: any) => {
            setNotifications(prev => [{
                type: 'error',
                message: `Document "${data.fileName}" processing failed: ${data.error}`,
                timestamp: data.timestamp,
                ...data
            }, ...prev]);
        };

        const handleSystemNotification = (data: any) => {
            setNotifications(prev => [{
                type: data.type || 'info',
                ...data
            }, ...prev]);
        };

        on('document:queued', handleDocumentQueued);
        on('document:completed', handleDocumentCompleted);
        on('document:failed', handleDocumentFailed);
        on('notification:system', handleSystemNotification);

        // Cleanup
        return () => {
            off('document:queued', handleDocumentQueued);
            off('document:completed', handleDocumentCompleted);
            off('document:failed', handleDocumentFailed);
            off('notification:system', handleSystemNotification);
        };
    }, [connected, on, off]);

    const clearNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    const removeNotification = useCallback((index: number) => {
        setNotifications(prev => prev.filter((_, i) => i !== index));
    }, []);

    return { notifications, clearNotifications, removeNotification };
};